import type { MiddlewareAPI } from 'redux';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import type { ActionsObservable } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CHAT_APP_NOT_ENABLED } from '../../common/constants';
import type {
	AddonStatus,
	ErrorResponse,
	GetChatAppStatusResponse,
	State,
} from '../../model/types';
import getChatAppStatus from '../../rest/get-chat-app-status';
import {
	type Action,
	GET_CHAT_APP_STATUS,
	getChatAppStatusFailure,
	loadChatSettingsPage,
	installChatApp,
	type GetChatAppStatus,
	type GetChatAppStatusFailure,
	type LoadChatSettingsPage,
	type InstallChatApp,
} from '../../state/actions';
import { getAddonKey, getBaseUrl } from '../../state/selectors';

const handleSuccess = (
	response: GetChatAppStatusResponse,
): LoadChatSettingsPage | GetChatAppStatusFailure => {
	const addonStatus: AddonStatus = {
		installed: true,
		enabled: false,
	};
	if (response.state === 'ENABLED') {
		addonStatus.enabled = true;
		return loadChatSettingsPage(addonStatus);
	}
	return getChatAppStatusFailure(CHAT_APP_NOT_ENABLED);
};

const handleFailure = (
	errorResponse: ErrorResponse,
	analyticsEvent: UIAnalyticsEvent,
): GetChatAppStatusFailure | InstallChatApp => {
	if (errorResponse.statusCode !== 404) {
		return getChatAppStatusFailure(errorResponse.message);
	}
	return installChatApp(analyticsEvent);
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (action$: ActionsObservable<Action>, store: MiddlewareAPI<State>) =>
	action$.ofType(GET_CHAT_APP_STATUS).mergeMap((action: GetChatAppStatus) => {
		const state: State = store.getState();
		const baseUrl = getBaseUrl(state);
		const addonKey = getAddonKey(state);
		return getChatAppStatus(baseUrl, addonKey)
			.map((getChatAppStatusResponse: GetChatAppStatusResponse) =>
				handleSuccess(getChatAppStatusResponse),
			)
			.catch((error: ErrorResponse) =>
				Observable.of(handleFailure(error, action.meta.analyticsEvent)),
			);
	});
